
import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDeleteTask, Task, TaskInstance } from "@/lib/api/checklist";
import { TaskList } from "./TaskList";
import { CreateEditTaskModal } from "./CreateEditTaskModal";
import { TaskDetailModal } from "./TaskDetailModal";
import { CreateInstancesModal } from "./CreateInstancesModal";
import { InstanceList } from "./InstanceList";
import { MyTasksView } from "./MyTasksView";
import { RecurrenceBreakdownView } from "./RecurrenceBreakdownView";
import { CompleteInstanceModal } from "./CompleteInstanceModal";
import { NonApplInstanceModal } from "./NonApplInstanceModal";
import { ReassignInstanceModal } from "./ReassignInstanceModal";
import { TemplatesDashboard } from "./templates";
import { toast } from "@/components/ui/use-toast";
import { useQueryParam } from "@/hooks/use-query-param";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export function TaskChecklistsDashboard() {
    const [activeTab, setActiveTab] = useQueryParam("checklist_tab", "tasks");
    const [taskIdParam, setTaskIdParam] = useQueryParam("task_id", "");
  
  // Task modals
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [isDeletingTask, setIsDeletingTask] = useState(false);
  const [editTask, setEditTask] = useState<Task | undefined>();
  const [viewTaskId, setViewTaskId] = useState<string | undefined>();
  const [createInstancesTask, setCreateInstancesTask] = useState<Task | undefined>();
  const [deleteTask, setDeleteTask] = useState<Task | undefined>();
  
  // Instance modals
  const [completeInstance, setCompleteInstance] = useState<TaskInstance | undefined>();
  const [nonApplInstance, setNonApplInstance] = useState<TaskInstance | undefined>();
  const [reassignInstance, setReassignInstance] = useState<TaskInstance | undefined>();

  const deleteTaskMutation = useDeleteTask();

  // Handle task_id query param for instances tab
  useEffect(() => {
    if (taskIdParam && activeTab === "instances") {
      setViewTaskId(taskIdParam);
    }
  }, [taskIdParam, activeTab]);

  // Clear task_id param when closing task detail modal
  useEffect(() => {
    if (!viewTaskId && taskIdParam) {
      setTaskIdParam("");
    }
  }, [viewTaskId]);

  const handleDeleteTask = async () => {
    if (!deleteTask) return;
      if (isDeletingTask) return;
      setIsDeletingTask(true);
    
    try {
      const result = await deleteTaskMutation.mutateAsync(deleteTask.id);
      if (!result?.success) {
        throw result;
      }

      toast({
        title: "Task deleted",
        description: "The task and all pending instances have been deleted.",
      });

      setDeleteTask(undefined);

      if (viewTaskId === deleteTask.id) {
        setViewTaskId(undefined);
      }
    } catch (error: any) {
      console.error("Failed to delete task", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete task.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingTask(false);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Task Checklists</h1>
        <p className="text-muted-foreground">
          Manage tasks, assign people, and track completion
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 max-w-2xl">
          <TabsTrigger value="tasks">All Tasks</TabsTrigger>
          <TabsTrigger value="my-tasks">My Tasks</TabsTrigger>
          <TabsTrigger value="instances">Assigned</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="breakdown">Breakdown</TabsTrigger>
        </TabsList>

        <TabsContent value="tasks" className="mt-6">
          <TaskList
            onViewTask={(task) => setViewTaskId(task.id)}
            onEditTask={setEditTask}
            onDeleteTask={setDeleteTask}
            onCreateInstances={setCreateInstancesTask}
            onCreateTask={() => setCreateTaskOpen(true)}
          />
        </TabsContent>

        <TabsContent value="my-tasks" className="mt-6">
          <MyTasksView
            onCompleteInstance={setCompleteInstance}
            onNonApplInstance={setNonApplInstance}
            onViewTask={setViewTaskId}
          />
        </TabsContent>

        <TabsContent value="instances" className="mt-6">
          <InstanceList
            onCompleteInstance={setCompleteInstance}
            onNonApplInstance={setNonApplInstance}
            onReassignInstance={setReassignInstance}
            onViewTask={setViewTaskId}
          />
        </TabsContent>

        <TabsContent value="breakdown" className="mt-6">
          <RecurrenceBreakdownView />
        </TabsContent>

        <TabsContent value="templates" className="mt-6">
          <TemplatesDashboard />
        </TabsContent>
      </Tabs>

      {/* Task Modals */}
      <CreateEditTaskModal
        open={createTaskOpen || !!editTask}
        onClose={() => {
          setCreateTaskOpen(false);
          setEditTask(undefined);
        }}
        task={editTask}
      />

      {viewTaskId && (
        <TaskDetailModal
          open={!!viewTaskId}
          onClose={() => setViewTaskId(undefined)}
          taskId={viewTaskId}
          onEdit={setEditTask}
          onDelete={setDeleteTask}
          onCreateInstances={setCreateInstancesTask}
        />
      )}

      {createInstancesTask && (
        <CreateInstancesModal
          open={!!createInstancesTask}
          onClose={() => setCreateInstancesTask(undefined)}
          task={createInstancesTask}
        />
      )}

      {/* Instance Modals */}
      {completeInstance && (
        <CompleteInstanceModal
          open={!!completeInstance}
          onClose={() => setCompleteInstance(undefined)}
          instance={completeInstance}
        />
      )}

      {nonApplInstance && (
        <NonApplInstanceModal
          open={!!nonApplInstance}
          onClose={() => setNonApplInstance(undefined)}
          instance={nonApplInstance}
        />
      )}

      {reassignInstance && (
        <ReassignInstanceModal
          open={!!reassignInstance}
          onClose={() => setReassignInstance(undefined)}
          instance={reassignInstance}
        />
      )}

 <Dialog open={!!deleteTask} onOpenChange={() => setDeleteTask(undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This will delete the task "{deleteTask?.title}" and all its pending
              instances. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDeleteTask(undefined)}>
                  Cancel
              </Button>

            <Button
              onClick={handleDeleteTask}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
    </div>
  );
}
